import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * A list screen's filters, kept in the address bar.
 *
 * Held in component state, a list's filters died with the page: open a record,
 * press Back, and the reader was on page 1 of everything again. In the URL they
 * come back with Back, survive a refresh, and can be sent as a link — which
 * still shows the recipient only what their own login allows, because the
 * server scopes every list.
 *
 * The address is rewritten in place (`replace`), never pushed, so Back leaves
 * the screen rather than stepping through every filter that was tried.
 *
 * In-app "Back to …" links point at the bare list address, so the last filters
 * are also remembered per tab and per screen in `sessionStorage`, and restored
 * when the screen opens with none of its own in the address. They are stored
 * under the drafts prefix, so signing out clears them with the drafts — a
 * search typed by one person is never shown to the next on a shared machine.
 *
 * Only the keys in `defaults` are read or written; anything else in the
 * address is left alone. A value equal to its default is dropped from the
 * address, so an unfiltered screen keeps a clean URL. A value that is not a
 * real option — a mistyped status in a pasted link — falls back to the default
 * rather than reaching the server.
 */

/** A filter value as it can travel in a URL. Numbers are positive integers (a page). */
export type UrlFilterValue = string | number;

export interface UrlFilterOptions<T extends Record<string, UrlFilterValue>> {
  /**
   * Namespaces the keys, for a screen with two filtered tables — `register`
   * gives `register.status`, so neither table reads the other's.
   */
  prefix?: string;
  /**
   * The only values a filter may take — a status, or a rows-per-page choice.
   * Anything else reads as the default.
   */
  allowed?: { [K in keyof T]?: readonly UrlFilterValue[] };
}

/**
 * The address as it is now, not as it was at the last render.
 *
 * react-router hands a `setSearchParams` updater the params of the last
 * render, so two writes in one event — a tab and a panel's filters, or two
 * instances restoring together — would each start from the same old address
 * and the last would erase the first. `BrowserRouter` updates the address
 * synchronously on every navigate, so reading it here chains them.
 */
function liveParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

/** Same prefix as `useSessionDraft`, so `clearSessionDrafts` on sign-out takes these too. */
const STORAGE_PREFIX = 'draft:filters:';

function readStored(storageKey: string): URLSearchParams | null {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? new URLSearchParams(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(storageKey: string, query: string) {
  try {
    if (query) sessionStorage.setItem(storageKey, query);
    else sessionStorage.removeItem(storageKey);
  } catch {
    // Full or blocked — the filters still work, they are just not remembered.
  }
}

/**
 * `defaults` and `options` are read on every render and must be stable —
 * declare them at module level, not inline.
 */
export function useUrlFilters<T extends Record<string, UrlFilterValue>>(
  defaults: T,
  options: UrlFilterOptions<T> = {},
): [T, (patch: Partial<T>) => void] {
  const { prefix, allowed } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname, hash } = useLocation();

  const keys = useMemo(() => Object.keys(defaults) as (keyof T & string)[], [defaults]);
  const paramName = useCallback(
    (key: string) => (prefix ? `${prefix}.${key}` : key),
    [prefix],
  );
  // Keyed by the filter names as well as the screen, so two instances on one
  // screen — a page's tab beside a panel's filters — never share a memory.
  const storageKey = `${STORAGE_PREFIX}${pathname}#${prefix ?? ''}:${keys.join(',')}`;

  const parse = useCallback(
    (params: URLSearchParams): T => {
      const result = { ...defaults };
      for (const key of keys) {
        const raw = params.get(paramName(key));
        if (raw === null) continue;
        const choices = allowed?.[key];
        if (choices && !choices.some((choice) => String(choice) === raw)) continue;
        if (typeof defaults[key] === 'number') {
          const parsed = Number(raw);
          if (Number.isInteger(parsed) && parsed >= 1) result[key] = parsed as T[typeof key];
        } else {
          result[key] = raw as T[typeof key];
        }
      }
      return result;
    },
    [defaults, keys, paramName, allowed],
  );

  /** Just this screen's keys, in a stable order — what is stored and compared. */
  const ownQuery = useCallback(
    (params: URLSearchParams) => {
      const own = new URLSearchParams();
      for (const key of keys) {
        const value = params.get(paramName(key));
        if (value !== null) own.set(paramName(key), value);
      }
      return own.toString();
    },
    [keys, paramName],
  );

  // Decided once, on the first render, so the first request already carries the
  // restored filters instead of fetching everything and then fetching again.
  // Not for a link to one spot on the screen (`#notice-…`): rewriting the
  // address would drop the anchor, and old filters could hide what it points at.
  const restoreRef = useRef<URLSearchParams | null | undefined>(undefined);
  if (restoreRef.current === undefined) {
    restoreRef.current = ownQuery(searchParams) || hash ? null : readStored(storageKey);
  }
  const pendingRestore = restoreRef.current;

  useEffect(() => {
    const restore = restoreRef.current;
    if (!restore) return;
    restoreRef.current = null;
    setSearchParams(
      () => {
        const next = liveParams();
        restore.forEach((value, name) => next.set(name, value));
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const query = ownQuery(searchParams);
  const filters = useMemo(
    () => parse(pendingRestore ?? new URLSearchParams(query)),
    [parse, pendingRestore, query],
  );

  // Remembered after the restore has landed, never before — otherwise the
  // empty first address would overwrite what is about to be restored.
  useEffect(() => {
    if (restoreRef.current) return;
    writeStored(storageKey, query);
  }, [storageKey, query]);

  const setFilters = useCallback(
    (patch: Partial<T>) => {
      setSearchParams(
        () => {
          const next = liveParams();
          const merged = { ...parse(next), ...patch };
          for (const key of keys) {
            const value = merged[key];
            if (value === defaults[key] || value === '') next.delete(paramName(key));
            else next.set(paramName(key), String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, parse, keys, defaults, paramName],
  );

  return [filters, setFilters];
}
